import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import frontOfficeService from '../../services/frontOfficeService';
import DialogModal from '../../components/DialogModal';
import BarcodeScanner from '../../components/BarcodeScanner';
import {
  MagnifyingGlass, Plus, Trash, SignOut, SignIn, QrCode, X, Check, X as XIcon,
  DoorOpen, CalendarCheck, Envelope, ArrowCounterClockwise,
} from '@phosphor-icons/react';

const emptyForm = {
  visitor_name: '',
  visitor_phone: '',
  purpose: '',
  host_staff_id: '',
  person_to_see: '',
  id_type_shown: '',
  notes: '',
};

const emptyGatePassForm = {
  person_type: 'student',
  person_id: '',
  reason: '',
  expected_return_time: '',
};

const emptyAppointmentForm = {
  staff_to_meet_id: '',
  visitor_name: '',
  visitor_phone: '',
  purpose: '',
  requested_datetime: '',
  notes: '',
};

const emptyCourierForm = {
  direction: 'inbound',
  sender: '',
  recipient_staff_id: '',
  item_description: '',
  tracking_number: '',
  courier_company: '',
  notes: '',
};

const STATUS_BADGE_STYLES = {
  pending: { background: '#fef3c7', color: '#92400e' },
  approved: { background: '#dcfce7', color: '#166534' },
  rejected: { background: '#fee2e2', color: '#dc2626' },
  out: { background: '#dbeafe', color: '#1e40af' },
  returned: { background: '#e5e7eb', color: '#374151' },
  requested: { background: '#fef3c7', color: '#92400e' },
  confirmed: { background: '#dcfce7', color: '#166534' },
  cancelled: { background: '#fee2e2', color: '#dc2626' },
  completed: { background: '#e0e7ff', color: '#3730a3' },
  no_show: { background: '#fee2e2', color: '#dc2626' },
  received: { background: '#dbeafe', color: '#1e40af' },
  notified: { background: '#fef3c7', color: '#92400e' },
  collected: { background: '#dcfce7', color: '#166534' },
  dispatched: { background: '#e0e7ff', color: '#3730a3' },
};

function renderStatusBadge(status) {
  const style = STATUS_BADGE_STYLES[status] || { background: '#e5e7eb', color: '#374151' };
  return (
    <span className="badge" style={style}>
      {(status || '').replace('_', ' ')}
    </span>
  );
}

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString() : '-';
}

function VisitorLogPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('visitors');

  // --- Visitors (existing) ---
  const [loading, setLoading] = useState(true);
  const [visitors, setVisitors] = useState([]);
  const [filteredVisitors, setFilteredVisitors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [badgeVisitor, setBadgeVisitor] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [promptDialog, setPromptDialog] = useState(null); // { title, message, defaultValue, onSubmit }

  // --- Gate Passes ---
  const [gatePasses, setGatePasses] = useState([]);
  const [gatePassLoading, setGatePassLoading] = useState(false);
  const [gatePassStatusFilter, setGatePassStatusFilter] = useState('all');
  const [showGatePassModal, setShowGatePassModal] = useState(false);
  const [gatePassForm, setGatePassForm] = useState(emptyGatePassForm);
  const [gatePassSubmitting, setGatePassSubmitting] = useState(false);
  const [gatePassConfirm, setGatePassConfirm] = useState(null); // { title, message, onConfirm }

  // --- Appointments ---
  const [appointments, setAppointments] = useState([]);
  const [appointmentLoading, setAppointmentLoading] = useState(false);
  const [appointmentStatusFilter, setAppointmentStatusFilter] = useState('all');
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [appointmentForm, setAppointmentForm] = useState(emptyAppointmentForm);
  const [appointmentSubmitting, setAppointmentSubmitting] = useState(false);
  const [appointmentConfirm, setAppointmentConfirm] = useState(null);

  // --- Courier / Mail ---
  const [courierItems, setCourierItems] = useState([]);
  const [courierLoading, setCourierLoading] = useState(false);
  const [courierDirectionFilter, setCourierDirectionFilter] = useState('all');
  const [courierStatusFilter, setCourierStatusFilter] = useState('all');
  const [showCourierModal, setShowCourierModal] = useState(false);
  const [courierForm, setCourierForm] = useState(emptyCourierForm);
  const [courierSubmitting, setCourierSubmitting] = useState(false);

  useEffect(() => {
    loadVisitors();
  }, []);

  useEffect(() => {
    if (tab === 'gate-passes') loadGatePasses();
    if (tab === 'appointments') loadAppointments();
    if (tab === 'courier') loadCourierItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    let filtered = visitors;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (v) =>
          v.visitor_name.toLowerCase().includes(term) ||
          (v.purpose || '').toLowerCase().includes(term) ||
          (v.person_to_see || '').toLowerCase().includes(term) ||
          v.badge_number.toLowerCase().includes(term)
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter((v) => v.status === statusFilter);
    }
    setFilteredVisitors(filtered);
  }, [visitors, searchTerm, statusFilter]);

  const loadVisitors = async () => {
    try {
      setLoading(true);
      const data = await frontOfficeService.listVisitors();
      setVisitors(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load visitor log');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.visitor_name || !formData.purpose) {
      toast.error('Visitor name and purpose are required');
      return;
    }
    setSubmitting(true);
    try {
      const visitor = await frontOfficeService.checkInVisitor(formData);
      toast.success('Visitor checked in');
      setShowModal(false);
      setFormData(emptyForm);
      setBadgeVisitor(visitor);
      loadVisitors();
    } catch (error) {
      toast.error(error.message || 'Failed to check in visitor');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckOut = async (visitor) => {
    try {
      await frontOfficeService.checkOutVisitor(visitor.id);
      toast.success(`${visitor.visitor_name} checked out`);
      loadVisitors();
    } catch (error) {
      toast.error(error.message || 'Failed to check out visitor');
    }
  };

  const handleScan = async (decodedText) => {
    try {
      const visitor = await frontOfficeService.getVisitorByBadge(decodedText.trim());
      if (visitor.status === 'checked_out') {
        toast.info(`${visitor.visitor_name} is already checked out`);
      } else {
        await frontOfficeService.checkOutVisitor(visitor.id);
        toast.success(`${visitor.visitor_name} checked out`);
      }
      setShowScanner(false);
      loadVisitors();
    } catch (error) {
      toast.error('Badge not found');
    }
  };

  const handleApprove = async (visitor) => {
    try {
      await frontOfficeService.approveVisitor(visitor.id);
      toast.success('Visitor record approved');
      loadVisitors();
    } catch (error) {
      toast.error(error.message || 'Failed to approve visitor record');
    }
  };

  const handleReject = (visitor) => {
    setPromptDialog({
      title: 'Reject Visitor Record',
      message: 'Reason for rejecting this visitor record (optional):',
      defaultValue: '',
      onSubmit: async (value) => {
        const reason = value || undefined;
        try {
          await frontOfficeService.rejectVisitor(visitor.id, reason);
          toast.success('Visitor record rejected');
          loadVisitors();
        } catch (error) {
          toast.error(error.message || 'Failed to reject visitor record');
        }
      },
    });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await frontOfficeService.deleteVisitor(deleteTarget);
      toast.success('Visitor record deleted');
      setDeleteTarget(null);
      loadVisitors();
    } catch (error) {
      toast.error('Failed to delete visitor record');
    } finally {
      setDeleteLoading(false);
    }
  };

  // --- Gate Pass handlers ---

  const loadGatePasses = async () => {
    try {
      setGatePassLoading(true);
      const data = await frontOfficeService.getGatePasses();
      setGatePasses(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load gate passes');
    } finally {
      setGatePassLoading(false);
    }
  };

  const filteredGatePasses =
    gatePassStatusFilter === 'all' ? gatePasses : gatePasses.filter((g) => g.status === gatePassStatusFilter);

  const handleGatePassSubmit = async (e) => {
    e.preventDefault();
    if (!gatePassForm.person_id || !gatePassForm.reason) {
      toast.error('Person ID and reason are required');
      return;
    }
    setGatePassSubmitting(true);
    try {
      await frontOfficeService.createGatePass({
        ...gatePassForm,
        expected_return_time: gatePassForm.expected_return_time || undefined,
      });
      toast.success('Gate pass issued');
      setShowGatePassModal(false);
      setGatePassForm(emptyGatePassForm);
      loadGatePasses();
    } catch (error) {
      toast.error(error.message || 'Failed to issue gate pass');
    } finally {
      setGatePassSubmitting(false);
    }
  };

  const handleApproveGatePass = async (gatePass) => {
    try {
      await frontOfficeService.approveGatePass(gatePass.id);
      toast.success('Gate pass approved');
      loadGatePasses();
    } catch (error) {
      toast.error(error.message || 'Failed to approve gate pass');
    }
  };

  const handleRejectGatePass = (gatePass) => {
    setGatePassConfirm({
      title: 'Reject Gate Pass',
      message: `Reject gate pass ${gatePass.pass_number}?`,
      onConfirm: async () => {
        try {
          await frontOfficeService.rejectGatePass(gatePass.id);
          toast.success('Gate pass rejected');
          loadGatePasses();
        } catch (error) {
          toast.error(error.message || 'Failed to reject gate pass');
        }
      },
    });
  };

  const handleExitGatePass = (gatePass) => {
    setGatePassConfirm({
      title: 'Record Exit',
      message: `Record gate pass ${gatePass.pass_number} as exited campus?`,
      onConfirm: async () => {
        try {
          await frontOfficeService.exitGatePass(gatePass.id);
          toast.success('Exit recorded');
          loadGatePasses();
        } catch (error) {
          toast.error(error.message || 'Failed to record exit');
        }
      },
    });
  };

  const handleReturnGatePass = (gatePass) => {
    setGatePassConfirm({
      title: 'Record Return',
      message: `Record gate pass ${gatePass.pass_number} as returned to campus?`,
      onConfirm: async () => {
        try {
          await frontOfficeService.returnGatePass(gatePass.id);
          toast.success('Return recorded');
          loadGatePasses();
        } catch (error) {
          toast.error(error.message || 'Failed to record return');
        }
      },
    });
  };

  // --- Appointment handlers ---

  const loadAppointments = async () => {
    try {
      setAppointmentLoading(true);
      const data = await frontOfficeService.getAppointments();
      setAppointments(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load appointments');
    } finally {
      setAppointmentLoading(false);
    }
  };

  const filteredAppointments =
    appointmentStatusFilter === 'all'
      ? appointments
      : appointments.filter((a) => a.status === appointmentStatusFilter);

  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();
    if (
      !appointmentForm.staff_to_meet_id ||
      !appointmentForm.visitor_name ||
      !appointmentForm.purpose ||
      !appointmentForm.requested_datetime
    ) {
      toast.error('Staff to meet, visitor name, purpose, and date/time are required');
      return;
    }
    setAppointmentSubmitting(true);
    try {
      await frontOfficeService.createAppointment(appointmentForm);
      toast.success('Appointment scheduled');
      setShowAppointmentModal(false);
      setAppointmentForm(emptyAppointmentForm);
      loadAppointments();
    } catch (error) {
      toast.error(error.message || 'Failed to schedule appointment');
    } finally {
      setAppointmentSubmitting(false);
    }
  };

  const handleConfirmAppointment = async (appointment) => {
    try {
      await frontOfficeService.updateAppointment(appointment.id, { status: 'confirmed' });
      toast.success('Appointment confirmed');
      loadAppointments();
    } catch (error) {
      toast.error(error.message || 'Failed to confirm appointment');
    }
  };

  const handleCancelAppointment = (appointment) => {
    setAppointmentConfirm({
      title: 'Cancel Appointment',
      message: `Cancel the appointment with ${appointment.visitor_name}?`,
      onConfirm: async () => {
        try {
          await frontOfficeService.updateAppointment(appointment.id, { status: 'cancelled' });
          toast.success('Appointment cancelled');
          loadAppointments();
        } catch (error) {
          toast.error(error.message || 'Failed to cancel appointment');
        }
      },
    });
  };

  const handleCheckInAppointment = async (appointment) => {
    try {
      const result = await frontOfficeService.checkInAppointment(appointment.id);
      toast.success(`${appointment.visitor_name} checked in`);
      if (result?.visitor) setBadgeVisitor(result.visitor);
      loadAppointments();
    } catch (error) {
      toast.error(error.message || 'Failed to check in appointment');
    }
  };

  // --- Courier handlers ---

  const loadCourierItems = async () => {
    try {
      setCourierLoading(true);
      const data = await frontOfficeService.getCourierItems();
      setCourierItems(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load courier items');
    } finally {
      setCourierLoading(false);
    }
  };

  const filteredCourierItems = courierItems.filter((item) => {
    if (courierDirectionFilter !== 'all' && item.direction !== courierDirectionFilter) return false;
    if (courierStatusFilter !== 'all' && item.status !== courierStatusFilter) return false;
    return true;
  });

  const handleCourierSubmit = async (e) => {
    e.preventDefault();
    if (!courierForm.sender || !courierForm.item_description) {
      toast.error('Sender and item description are required');
      return;
    }
    setCourierSubmitting(true);
    try {
      await frontOfficeService.createCourierItem(courierForm);
      toast.success('Courier item logged');
      setShowCourierModal(false);
      setCourierForm(emptyCourierForm);
      loadCourierItems();
    } catch (error) {
      toast.error(error.message || 'Failed to log courier item');
    } finally {
      setCourierSubmitting(false);
    }
  };

  const handleCourierStatusChange = async (item, status) => {
    try {
      await frontOfficeService.updateCourierItem(item.id, { status });
      toast.success('Status updated');
      loadCourierItems();
    } catch (error) {
      toast.error(error.message || 'Failed to update status');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading visitor log...</div>
      </Layout>
    );
  }

  const checkedInCount = visitors.filter((v) => v.status === 'checked_in').length;

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Front Office</h1>
            <p>Manage visitors, gate passes, appointments, and courier/mail</p>
          </div>
        </div>

        <div className="tabs-container">
          <button className={`tab-btn ${tab === 'visitors' ? 'active' : ''}`} onClick={() => setTab('visitors')}>
            <MagnifyingGlass size={18} /> Visitors
          </button>
          <button className={`tab-btn ${tab === 'gate-passes' ? 'active' : ''}`} onClick={() => setTab('gate-passes')}>
            <DoorOpen size={18} /> Gate Passes
          </button>
          <button className={`tab-btn ${tab === 'appointments' ? 'active' : ''}`} onClick={() => setTab('appointments')}>
            <CalendarCheck size={18} /> Appointments
          </button>
          <button className={`tab-btn ${tab === 'courier' ? 'active' : ''}`} onClick={() => setTab('courier')}>
            <Envelope size={18} /> Courier/Mail
          </button>
        </div>

        {tab === 'visitors' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, margin: '16px 0' }}>
              <button className="btn btn-secondary" onClick={() => setShowScanner(true)}>
                <QrCode size={20} />
                Scan to Check Out
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setFormData(emptyForm);
                  setShowModal(true);
                }}
              >
                <Plus size={20} />
                Check In Visitor
              </button>
            </div>

            <div className="stats-bar">
              <div className="stat">
                <h3>Total Visitors</h3>
                <p className="stat-value">{visitors.length}</p>
              </div>
              <div className="stat">
                <h3>Currently On Campus</h3>
                <p className="stat-value">{checkedInCount}</p>
              </div>
              <div className="stat">
                <h3>Checked Out</h3>
                <p className="stat-value">{visitors.length - checkedInCount}</p>
              </div>
            </div>

            <div className="filters-bar">
              <div className="search-input">
                <MagnifyingGlass size={20} />
                <input
                  type="text"
                  placeholder="Search by name, purpose, badge..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Statuses</option>
                <option value="checked_in">Checked In</option>
                <option value="checked_out">Checked Out</option>
              </select>
            </div>

            <div className="table-container">
              {filteredVisitors.length === 0 ? (
                <div className="empty-state">
                  <p>No visitor records found</p>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Badge #</th>
                      <th>Visitor Name</th>
                      <th>Phone</th>
                      <th>Purpose</th>
                      <th>Person to See</th>
                      <th>Check-in</th>
                      <th>Check-out</th>
                      <th>Status</th>
                      <th>Approval</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVisitors.map((visitor) => (
                      <tr key={visitor.id}>
                        <td>
                          <button
                            className="btn-icon"
                            title="Show badge QR"
                            onClick={() => setBadgeVisitor(visitor)}
                          >
                            {visitor.badge_number}
                          </button>
                        </td>
                        <td>{visitor.visitor_name}</td>
                        <td>{visitor.visitor_phone || '-'}</td>
                        <td>{visitor.purpose}</td>
                        <td>{visitor.person_to_see || '-'}</td>
                        <td>{new Date(visitor.check_in_time).toLocaleString()}</td>
                        <td>{visitor.check_out_time ? new Date(visitor.check_out_time).toLocaleString() : '-'}</td>
                        <td className="text-capitalize">{visitor.status.replace('_', ' ')}</td>
                        <td>
                          {visitor.approval_status === 'pending' && <span className="badge inactive">Pending</span>}
                          {visitor.approval_status === 'approved' && <span className="badge active">Approved</span>}
                          {visitor.approval_status === 'rejected' && (
                            <span className="badge" style={{ background: '#fee2e2', color: '#dc2626' }} title={visitor.rejection_reason || ''}>Rejected</span>
                          )}
                        </td>
                        <td className="action-buttons">
                          {visitor.approval_status === 'pending' && visitor.created_by !== user?.id && (
                            <>
                              <button className="btn-icon" title="Approve" onClick={() => handleApprove(visitor)}>
                                <Check size={18} color="#16a34a" />
                              </button>
                              <button className="btn-icon btn-danger" title="Reject" onClick={() => handleReject(visitor)}>
                                <XIcon size={18} />
                              </button>
                            </>
                          )}
                          {visitor.status === 'checked_in' && (
                            <button
                              className="btn-icon btn-primary"
                              title="Check Out"
                              onClick={() => handleCheckOut(visitor)}
                            >
                              <SignOut size={18} />
                            </button>
                          )}
                          <button
                            className="btn-icon btn-danger"
                            title="Delete"
                            onClick={() => setDeleteTarget(visitor.id)}
                          >
                            <Trash size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {tab === 'gate-passes' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, margin: '16px 0' }}>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setGatePassForm(emptyGatePassForm);
                  setShowGatePassModal(true);
                }}
              >
                <Plus size={20} />
                Issue Gate Pass
              </button>
            </div>

            <div className="filters-bar">
              <select
                value={gatePassStatusFilter}
                onChange={(e) => setGatePassStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="out">Out</option>
                <option value="returned">Returned</option>
              </select>
            </div>

            <div className="table-container">
              {gatePassLoading ? (
                <div className="loading">Loading gate passes...</div>
              ) : filteredGatePasses.length === 0 ? (
                <div className="empty-state">
                  <p>No gate passes found</p>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Pass #</th>
                      <th>Person Type</th>
                      <th>Person ID</th>
                      <th>Reason</th>
                      <th>Expected Return</th>
                      <th>Exit Time</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGatePasses.map((gp) => (
                      <tr key={gp.id}>
                        <td>{gp.pass_number}</td>
                        <td className="text-capitalize">{gp.person_type}</td>
                        <td>{gp.person_id}</td>
                        <td>{gp.reason}</td>
                        <td>{formatDateTime(gp.expected_return_time)}</td>
                        <td>{formatDateTime(gp.exit_time)}</td>
                        <td>{renderStatusBadge(gp.status)}</td>
                        <td className="action-buttons">
                          {gp.status === 'pending' && (
                            <>
                              <button className="btn-icon" title="Approve" onClick={() => handleApproveGatePass(gp)}>
                                <Check size={18} color="#16a34a" />
                              </button>
                              <button className="btn-icon btn-danger" title="Reject" onClick={() => handleRejectGatePass(gp)}>
                                <XIcon size={18} />
                              </button>
                            </>
                          )}
                          {gp.status === 'approved' && (
                            <button className="btn-icon btn-primary" title="Record Exit" onClick={() => handleExitGatePass(gp)}>
                              <SignOut size={18} />
                            </button>
                          )}
                          {gp.status === 'out' && (
                            <button className="btn-icon btn-primary" title="Record Return" onClick={() => handleReturnGatePass(gp)}>
                              <ArrowCounterClockwise size={18} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {tab === 'appointments' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, margin: '16px 0' }}>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setAppointmentForm(emptyAppointmentForm);
                  setShowAppointmentModal(true);
                }}
              >
                <Plus size={20} />
                Schedule Appointment
              </button>
            </div>

            <div className="filters-bar">
              <select
                value={appointmentStatusFilter}
                onChange={(e) => setAppointmentStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Statuses</option>
                <option value="requested">Requested</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
                <option value="no_show">No Show</option>
              </select>
            </div>

            <div className="table-container">
              {appointmentLoading ? (
                <div className="loading">Loading appointments...</div>
              ) : filteredAppointments.length === 0 ? (
                <div className="empty-state">
                  <p>No appointments found</p>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Visitor</th>
                      <th>Phone</th>
                      <th>Staff to Meet</th>
                      <th>Purpose</th>
                      <th>Requested Date/Time</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAppointments.map((appt) => (
                      <tr key={appt.id}>
                        <td>{appt.visitor_name}</td>
                        <td>{appt.visitor_phone || '-'}</td>
                        <td>{appt.staff_to_meet_id}</td>
                        <td>{appt.purpose}</td>
                        <td>{formatDateTime(appt.requested_datetime)}</td>
                        <td>{renderStatusBadge(appt.status)}</td>
                        <td className="action-buttons">
                          {appt.status === 'requested' && (
                            <button className="btn-icon" title="Confirm" onClick={() => handleConfirmAppointment(appt)}>
                              <Check size={18} color="#16a34a" />
                            </button>
                          )}
                          {(appt.status === 'requested' || appt.status === 'confirmed') && (
                            <>
                              <button className="btn-icon btn-primary" title="Check In" onClick={() => handleCheckInAppointment(appt)}>
                                <SignIn size={18} />
                              </button>
                              <button className="btn-icon btn-danger" title="Cancel" onClick={() => handleCancelAppointment(appt)}>
                                <XIcon size={18} />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {tab === 'courier' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, margin: '16px 0' }}>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setCourierForm(emptyCourierForm);
                  setShowCourierModal(true);
                }}
              >
                <Plus size={20} />
                Log Courier Item
              </button>
            </div>

            <div className="filters-bar">
              <select
                value={courierDirectionFilter}
                onChange={(e) => setCourierDirectionFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Directions</option>
                <option value="inbound">Inbound</option>
                <option value="outbound">Outbound</option>
              </select>
              <select
                value={courierStatusFilter}
                onChange={(e) => setCourierStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Statuses</option>
                <option value="received">Received</option>
                <option value="notified">Notified</option>
                <option value="collected">Collected</option>
                <option value="dispatched">Dispatched</option>
              </select>
            </div>

            <div className="table-container">
              {courierLoading ? (
                <div className="loading">Loading courier items...</div>
              ) : filteredCourierItems.length === 0 ? (
                <div className="empty-state">
                  <p>No courier/mail items found</p>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Direction</th>
                      <th>Sender</th>
                      <th>Recipient Staff ID</th>
                      <th>Description</th>
                      <th>Tracking #</th>
                      <th>Received / Dispatched</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCourierItems.map((item) => (
                      <tr key={item.id}>
                        <td className="text-capitalize">{item.direction}</td>
                        <td>{item.sender}</td>
                        <td>{item.recipient_staff_id || '-'}</td>
                        <td>{item.item_description}</td>
                        <td>{item.tracking_number || '-'}</td>
                        <td>{formatDateTime(item.received_at || item.dispatched_at)}</td>
                        <td>{renderStatusBadge(item.status)}</td>
                        <td className="action-buttons">
                          {item.direction === 'inbound' && item.status === 'received' && (
                            <button className="btn-icon" title="Notify Recipient" onClick={() => handleCourierStatusChange(item, 'notified')}>
                              <Envelope size={18} />
                            </button>
                          )}
                          {item.direction === 'inbound' && item.status === 'notified' && (
                            <button className="btn-icon btn-primary" title="Mark Collected" onClick={() => handleCourierStatusChange(item, 'collected')}>
                              <Check size={18} color="#16a34a" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>

      {/* Check-in Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Check In Visitor</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close">
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Visitor Name *</label>
                <input
                  type="text"
                  value={formData.visitor_name}
                  onChange={(e) => setFormData({ ...formData, visitor_name: e.target.value })}
                  placeholder="e.g., John Smith"
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={formData.visitor_phone}
                  onChange={(e) => setFormData({ ...formData, visitor_phone: e.target.value })}
                  placeholder="e.g., +233501234567"
                />
              </div>

              <div className="form-group">
                <label>Purpose of Visit *</label>
                <input
                  type="text"
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  placeholder="e.g., Meeting with the Headmaster"
                  required
                />
              </div>

              <div className="form-group">
                <label>Person / Office to See</label>
                <input
                  type="text"
                  value={formData.person_to_see}
                  onChange={(e) => setFormData({ ...formData, person_to_see: e.target.value })}
                  placeholder="e.g., The Headmaster"
                />
              </div>

              <div className="form-group">
                <label>ID Type Shown</label>
                <input
                  type="text"
                  value={formData.id_type_shown}
                  onChange={(e) => setFormData({ ...formData, id_type_shown: e.target.value })}
                  placeholder="e.g., National ID"
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Checking In...' : 'Check In'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Gate Pass Modal */}
      {showGatePassModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Issue Gate Pass</h2>
              <button type="button" className="btn-close" onClick={() => setShowGatePassModal(false)} aria-label="Close">
                ×
              </button>
            </div>
            <form onSubmit={handleGatePassSubmit}>
              <div className="form-group">
                <label>Person Type *</label>
                <select
                  value={gatePassForm.person_type}
                  onChange={(e) => setGatePassForm({ ...gatePassForm, person_type: e.target.value })}
                >
                  <option value="student">Student</option>
                  <option value="staff">Staff</option>
                </select>
              </div>

              <div className="form-group">
                <label>Person ID *</label>
                <input
                  type="text"
                  value={gatePassForm.person_id}
                  onChange={(e) => setGatePassForm({ ...gatePassForm, person_id: e.target.value })}
                  placeholder="Student or Staff record ID"
                  required
                />
              </div>

              <div className="form-group">
                <label>Reason *</label>
                <input
                  type="text"
                  value={gatePassForm.reason}
                  onChange={(e) => setGatePassForm({ ...gatePassForm, reason: e.target.value })}
                  placeholder="e.g., Medical appointment"
                  required
                />
              </div>

              <div className="form-group">
                <label>Expected Return Time</label>
                <input
                  type="datetime-local"
                  value={gatePassForm.expected_return_time}
                  onChange={(e) => setGatePassForm({ ...gatePassForm, expected_return_time: e.target.value })}
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={gatePassSubmitting}>
                  {gatePassSubmitting ? 'Issuing...' : 'Issue Gate Pass'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowGatePassModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Appointment Modal */}
      {showAppointmentModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Schedule Appointment</h2>
              <button type="button" className="btn-close" onClick={() => setShowAppointmentModal(false)} aria-label="Close">
                ×
              </button>
            </div>
            <form onSubmit={handleAppointmentSubmit}>
              <div className="form-group">
                <label>Staff to Meet (Staff ID) *</label>
                <input
                  type="text"
                  value={appointmentForm.staff_to_meet_id}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, staff_to_meet_id: e.target.value })}
                  placeholder="Staff record ID"
                  required
                />
              </div>

              <div className="form-group">
                <label>Visitor Name *</label>
                <input
                  type="text"
                  value={appointmentForm.visitor_name}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, visitor_name: e.target.value })}
                  placeholder="e.g., Jane Doe"
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={appointmentForm.visitor_phone}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, visitor_phone: e.target.value })}
                  placeholder="e.g., +233501234567"
                />
              </div>

              <div className="form-group">
                <label>Purpose *</label>
                <input
                  type="text"
                  value={appointmentForm.purpose}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, purpose: e.target.value })}
                  placeholder="e.g., Admissions inquiry"
                  required
                />
              </div>

              <div className="form-group">
                <label>Requested Date/Time *</label>
                <input
                  type="datetime-local"
                  value={appointmentForm.requested_datetime}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, requested_datetime: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={appointmentForm.notes}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, notes: e.target.value })}
                  placeholder="Any additional notes..."
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={appointmentSubmitting}>
                  {appointmentSubmitting ? 'Scheduling...' : 'Schedule'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAppointmentModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Courier Item Modal */}
      {showCourierModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Log Courier / Mail Item</h2>
              <button type="button" className="btn-close" onClick={() => setShowCourierModal(false)} aria-label="Close">
                ×
              </button>
            </div>
            <form onSubmit={handleCourierSubmit}>
              <div className="form-group">
                <label>Direction *</label>
                <select
                  value={courierForm.direction}
                  onChange={(e) => setCourierForm({ ...courierForm, direction: e.target.value })}
                >
                  <option value="inbound">Inbound</option>
                  <option value="outbound">Outbound</option>
                </select>
              </div>

              <div className="form-group">
                <label>Sender *</label>
                <input
                  type="text"
                  value={courierForm.sender}
                  onChange={(e) => setCourierForm({ ...courierForm, sender: e.target.value })}
                  placeholder="e.g., DHL, or the school (for outbound)"
                  required
                />
              </div>

              <div className="form-group">
                <label>Recipient Staff ID</label>
                <input
                  type="text"
                  value={courierForm.recipient_staff_id}
                  onChange={(e) => setCourierForm({ ...courierForm, recipient_staff_id: e.target.value })}
                  placeholder="Staff record ID"
                />
              </div>

              <div className="form-group">
                <label>Item Description *</label>
                <input
                  type="text"
                  value={courierForm.item_description}
                  onChange={(e) => setCourierForm({ ...courierForm, item_description: e.target.value })}
                  placeholder="e.g., Sealed envelope, parcel"
                  required
                />
              </div>

              <div className="form-group">
                <label>Tracking Number</label>
                <input
                  type="text"
                  value={courierForm.tracking_number}
                  onChange={(e) => setCourierForm({ ...courierForm, tracking_number: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Courier Company</label>
                <input
                  type="text"
                  value={courierForm.courier_company}
                  onChange={(e) => setCourierForm({ ...courierForm, courier_company: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={courierForm.notes}
                  onChange={(e) => setCourierForm({ ...courierForm, notes: e.target.value })}
                  placeholder="Any additional notes..."
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={courierSubmitting}>
                  {courierSubmitting ? 'Logging...' : 'Log Item'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowCourierModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Badge QR Modal */}
      {badgeVisitor && (
        <div className="modal-overlay" onClick={() => setBadgeVisitor(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 340, textAlign: 'center' }}>
            <div className="modal-header">
              <h2>Visitor Badge</h2>
              <button type="button" className="btn-close" onClick={() => setBadgeVisitor(null)} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: '8px 0 20px' }}>
              <p style={{ margin: '0 0 16px', fontWeight: 600 }}>{badgeVisitor.visitor_name}</p>
              <div style={{ background: '#fff', padding: 16, borderRadius: 12, display: 'inline-block' }}>
                <QRCode value={badgeVisitor.badge_number} size={180} level="H" />
              </div>
              <p style={{ marginTop: 12, fontFamily: 'monospace', fontSize: 14 }}>{badgeVisitor.badge_number}</p>
            </div>
          </div>
        </div>
      )}

      {/* Scan to Check Out Modal */}
      {showScanner && (
        <div className="modal-overlay" onClick={() => setShowScanner(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <div className="modal-header">
              <h2>Scan Badge to Check Out</h2>
              <button type="button" className="btn-close" onClick={() => setShowScanner(false)} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: '8px 0' }}>
              <BarcodeScanner active={showScanner} onScan={handleScan} manualLabel="Enter badge number" />
            </div>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Visitor Record"
        message="This will permanently delete this visitor record. Continue?"
        type="warning"
        isLoading={deleteLoading}
        primaryAction={{ label: 'Delete', onClick: confirmDelete, loadingLabel: 'Deleting...' }}
        secondaryAction={{ label: 'Cancel' }}
      />

      <DialogModal
        isOpen={!!promptDialog}
        onClose={() => setPromptDialog(null)}
        title={promptDialog?.title}
        message={promptDialog?.message}
        type="info"
        input={{ placeholder: 'Reason (optional)', defaultValue: promptDialog?.defaultValue || '' }}
        primaryAction={{ label: 'Submit', onClick: (value) => promptDialog?.onSubmit(value) }}
        secondaryAction={{ label: 'Cancel' }}
      />

      <DialogModal
        isOpen={!!gatePassConfirm}
        onClose={() => setGatePassConfirm(null)}
        title={gatePassConfirm?.title}
        message={gatePassConfirm?.message}
        type="warning"
        primaryAction={{ label: 'Confirm', onClick: () => gatePassConfirm?.onConfirm() }}
        secondaryAction={{ label: 'Cancel' }}
      />

      <DialogModal
        isOpen={!!appointmentConfirm}
        onClose={() => setAppointmentConfirm(null)}
        title={appointmentConfirm?.title}
        message={appointmentConfirm?.message}
        type="warning"
        primaryAction={{ label: 'Confirm', onClick: () => appointmentConfirm?.onConfirm() }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default VisitorLogPage;
